/**
 * Canonical resource file paths — the model-facing address of a skill or knowledge file:
 * `skills/<skill-slug>/<relative-path>` for a skill, `resources/<resource-slug>/<relative-path>` for
 * a knowledge resource (#5142 step 5 PR B; knowledge keeps its mount until C1 deletes it).
 *
 * Why paths and not ids: publishing a hub regenerates every UUID
 * (`hub-branching.ts` re-mints `resource_id`, `resource_file_id` and `file_id`),
 * so a prompt that names a file by id breaks the moment preview is promoted to
 * production. A path is built from names — resource, folders, file — and those
 * clone verbatim, so it survives the hop and reads like the hub-as-code
 * workspace the author already edits.
 *
 * The one part of a path NOT built from names is a collision suffix (`-2`) on a
 * duplicate resource name or a duplicate file path. Those are ranks within the
 * colliding set, so they shift when a sibling is renamed or removed, and they
 * are re-derived at publish. See `compareForSlugOrder` and
 * `compareForOrdinalOrder` for exactly how far the stability goes.
 *
 * Pure and synchronous over pre-fetched rows — every caller already holds the
 * HubDO lists it needs, and keeping this free of I/O lets the prompt builder,
 * the list tool and the read tool derive the SAME path from the same data. It
 * lives in `@wayai/core` so the CLI's knowledge migration (#5142 step 4) writes
 * each file at the address the tools resolve today, from the same rule.
 *
 * TRAP: `resource_file.file_path` (a relative display path) and
 * `resource_file.file.file_path` (the R2 object key) are different columns with
 * the same name. Only the former is a path in this module's sense; the R2 key
 * is never exposed to the model.
 */

import { slugifySkillName } from './resources';

/**
 * The fields of a resource file row this module reads. HubDO's `ResourceFileRow`
 * satisfies it; the index hands each entry back its own row (`ResourcePathEntry.file`).
 */
export interface ResourcePathFileRow {
  resource_id: string;
  resource_file_id: string;
  file_id: string;
  resource_file_title?: string | null;
  folder_id?: string | null;
  /** The relative display path (see the TRAP above), never the R2 key. */
  file_path?: string | null;
  order_index?: number | null;
  enabled?: boolean | number | null;
  created_at?: string | null;
  file?: { file_name?: string | null } | null;
}

/** The fields of a resource folder row this module reads. HubDO's `ResourceFolderRow` satisfies it. */
export interface ResourcePathFolderRow {
  resource_folder_id: string;
  folder_name?: string | null;
  parent_folder_id?: string | null;
}

/** The mount a knowledge resource's files hang off, until #5142 step 5's C1 deletes knowledge. */
export const RESOURCE_NAMESPACE = 'resources';

/** The mount a skill's files hang off. */
export const SKILL_NAMESPACE = 'skills';

/** The two resource mounts, skills first. */
export const RESOURCE_MOUNTS = [SKILL_NAMESPACE, RESOURCE_NAMESPACE] as const;
export type ResourceNamespace = (typeof RESOURCE_MOUNTS)[number];

/** Slug used when a resource's display name slugs to nothing (e.g. "///"). */
const FALLBACK_SLUG = 'resource';

/** Leaf used when a row carries no usable name at all; ordinals disambiguate.
 *  Shared with the conversation-attachment aliases so both address grammars fall
 *  back to the same word. */
export const FALLBACK_LEAF = 'file';

/**
 * Depth cap for the `folder_id` → `parent_folder_id` walk. A cycle is already
 * caught by the visited set; this additionally bounds a pathologically deep
 * (or corrupt) chain so one bad row can't dominate a turn.
 */
const MAX_FOLDER_DEPTH = 32;

/**
 * UI uploads store `{timestamp}_{name}` (optionally under a `{folderUuid}/`
 * segment) in `resource_file.file_path` — a storage detail, not an author's
 * path. Either shape means the column is polluted and the folder chain (or the
 * bare file name) is the honest source.
 */
const UI_UPLOAD_TIMESTAMP_PREFIX = /^\d{10,}_/;
const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Minimal resource shape the slug map needs — `ResourceRow` satisfies it. */
export interface ResourceSlugSource {
  resource_id: string;
  resource_name?: string | null;
  skill_name?: string | null;
  resource_type?: string | null;
  created_at?: string | null;
}

/** One addressable resource file. */
export interface ResourcePathEntry<F extends ResourcePathFileRow = ResourcePathFileRow> {
  resource_id: string;
  resource_slug: string;
  resource_file_id: string;
  file_id: string;
  /** Path within the resource, e.g. `references/menu.md`. */
  relative_path: string;
  /** Full model-facing address, e.g. `skills/product-docs/references/menu.md`. */
  canonical_path: string;
  /** The row this entry was derived from (carries the R2 key + mime). */
  file: F;
}

export interface ResourcePathIndex<F extends ResourcePathFileRow = ResourcePathFileRow> {
  entries: ResourcePathEntry<F>[];
  slugByResourceId: Map<string, string>;
  /** `skills/<slug>` or `resources/<slug>` of each resource. */
  mountPathByResourceId: Map<string, string>;
  /** Keyed by the mount path (`skills/<slug>`, `resources/<slug>`); slugs are lowercase. */
  resourceIdByMountPath: Map<string, string>;
  /** Keyed by LOWERCASED canonical path — paths are matched case-insensitively. */
  byCanonicalPath: Map<string, ResourcePathEntry<F>>;
  byFileId: Map<string, ResourcePathEntry<F>>;
  entriesByResourceId: Map<string, ResourcePathEntry<F>[]>;
}

// ---------------------------------------------------------------------------
// Slugs
// ---------------------------------------------------------------------------

/**
 * Display name — skill_name for skills (falling back to resource_name), else
 * resource_name.
 *
 * Exported because `{{resources()}}` labels resources with this exact rule and
 * this module slugs the same string into the path. One is what the model READS,
 * the other is what it ADDRESSES; two copies of the rule could drift apart and
 * break precisely the agreement this module exists to hold.
 */
export function resourceDisplayName(resource: ResourceSlugSource): string {
  if (resource.resource_type === 'skill') {
    return resource.skill_name || resource.resource_name || '';
  }
  return resource.resource_name || '';
}

/**
 * `resource_name` has no unique index, so slugs collide. Order is
 * (created_at ASC, resource_id ASC) — the first claimant keeps the bare slug.
 *
 * Scope of that stability, precisely: **adding** a resource never renumbers an
 * existing one (a new row always sorts last). Renaming or deleting a resource
 * DOES renumber every later resource sharing its base slug, because the ordinal
 * is a rank within the colliding set — rename the oldest "Support" and the
 * second one takes over `support`. Publishing renumbers too: `hub-branching.ts`
 * re-mints `resource_id` and drops `created_at`, so production re-derives both
 * keys at clone time.
 *
 * No stronger guarantee is available: nothing that survives a clone
 * distinguishes two resources with the same display name. Colliding names
 * therefore have no stable address, which is why the docs tell authors to keep
 * resource names unique when they hard-wire paths into instructions.
 */
function compareForSlugOrder(a: ResourceSlugSource, b: ResourceSlugSource): number {
  const at = a.created_at ?? '';
  const bt = b.created_at ?? '';
  if (at !== bt) return at < bt ? -1 : 1;
  return a.resource_id < b.resource_id ? -1 : a.resource_id > b.resource_id ? 1 : 0;
}

function isSkill(resource: Pick<ResourceSlugSource, 'resource_type'>): boolean {
  return resource.resource_type === 'skill';
}

/** The mount a resource's files hang off: `skills` for a skill, `resources` for knowledge. */
export function resourceNamespace(resource: Pick<ResourceSlugSource, 'resource_type'>): ResourceNamespace {
  return isSkill(resource) ? SKILL_NAMESPACE : RESOURCE_NAMESPACE;
}

/** `skills/<slug>` or `resources/<slug>`: the mount path of a resource whose slug is `slug`. */
export function resourceMountPath(resource: Pick<ResourceSlugSource, 'resource_type'>, slug: string): string {
  return `${resourceNamespace(resource)}/${slug}`;
}

/**
 * A skill's slug before any collision suffix: its `skill_name`, which every writer checks with
 * `validateSkillName` (so this kebab pass leaves a valid one as it is), else its `resource_name`.
 * The one rule a skill is addressed by: its `skills/` mount here, and its CLI folder once
 * #5142 step 5's D1b moves the CLI onto it. The provider upload folder still takes
 * `resource_name` (`skillSync.ts`): changing it waits on both vendors accepting a new version
 * whose top-level folder differs.
 */
export function skillSlug(skill: Pick<ResourceSlugSource, 'resource_name' | 'skill_name'>): string {
  return baseSlug({ ...skill, resource_id: '', resource_type: 'skill' });
}

/** A resource's slug before any collision suffix: its display name, slugged. */
function baseSlug(resource: ResourceSlugSource): string {
  return slugifySkillName(resourceDisplayName(resource)) || FALLBACK_SLUG;
}

/** Each resource of `ordered` (already in slug order) mapped to its slug, ranked among them only. */
function rankSlugs(ordered: ResourceSlugSource[]): Map<string, string> {
  const used = new Set<string>();
  const slugByResourceId = new Map<string, string>();
  for (const resource of ordered) {
    const base = baseSlug(resource);
    let slug = base;
    for (let ordinal = 2; used.has(slug); ordinal++) slug = `${base}-${ordinal}`;
    used.add(slug);
    slugByResourceId.set(resource.resource_id, slug);
  }
  return slugByResourceId;
}

/**
 * Map every resource to its path slug, within its own mount.
 *
 * Uses `slugifySkillName`, the same kebab producer the CLI runs over a resource
 * name for its workspace directory, so the common case — a uniquely-named
 * knowledge resource — slugs identically on disk and in the prompt. Two inputs
 * diverge deliberately and the CLI has no equivalent: a SKILL slugs its
 * `skill_name` here but its `resource_name` on disk (until D1b), and a duplicate
 * name takes a `-2` suffix here that the filesystem does not produce.
 *
 * Two rankings, one per mount:
 * - **A skill** is ranked among the hub's skills only: it is the only kind under `skills/`.
 * - **A knowledge resource** is ranked over EVERY resource, skills included — the rule all
 *   resources shared under `resources/` before skills moved out (#5142 step 5 PR B). So no
 *   knowledge address moves, and no citation of one does, until C1 deletes knowledge. Its
 *   suffix may count a skill that no longer shares its mount: `resources/faq-2` beside
 *   `skills/faq`.
 *
 * Collisions take `-2`, `-3`; deliberately NOT core's `ensureUniqueSlug`, which
 * produces snake_case (`_2`) for ClickHouse-safe identifiers and would break the
 * kebab grammar here.
 */
export function buildResourceSlugMap(resources: ResourceSlugSource[]): Map<string, string> {
  const ordered = resources.filter((resource) => !!resource?.resource_id).sort(compareForSlugOrder);
  const overAll = rankSlugs(ordered);
  const overSkills = rankSlugs(ordered.filter(isSkill));
  const slugByResourceId = new Map<string, string>();
  for (const resource of ordered) {
    const slug = isSkill(resource) ? overSkills.get(resource.resource_id) : overAll.get(resource.resource_id);
    if (slug) slugByResourceId.set(resource.resource_id, slug);
  }
  return slugByResourceId;
}

// ---------------------------------------------------------------------------
// Skill citations under the knowledge mount
// ---------------------------------------------------------------------------

/**
 * `resources/<slug>` at the start of a path: not inside another word or path (an optional leading
 * `/` is allowed), and the slug whole (`faq` never matches `faq-2`). The boundary rule the CLI's
 * cut-over reads the same citations by (`apps/cli/src/knowledge/cut-over.ts`).
 */
const RESOURCE_CITATION_RE = /(?<![\w./-])\/?resources\/([a-z0-9]+(?:-[a-z0-9]+)*)(?![a-z0-9_]|-[a-z0-9]|\.\w)/gi;

/** A `resources/<slug>` citation that names a skill, and the mount the skill is read at. */
export interface SkillCitation {
  /** `resources/<slug>`, the slug lowercased. */
  citation: string;
  /** `skills/<slug>`. */
  skill_path: string;
}

/**
 * The `resources/<slug>` citations in `text` that name a skill of `resources` (the hub's FULL list),
 * each once: by the skill's address before skills moved to `skills/` (its slug ranked over every
 * resource, the rule knowledge keeps), or by its own slug. A slug a knowledge resource holds is
 * that resource's and is never reported. What a writer refuses (#5142 step 5 PR B): text pulled or
 * opened before the move would otherwise write the old address back.
 */
export function skillCitationsUnderResources(
  text: string | null | undefined,
  resources: ResourceSlugSource[],
): SkillCitation[] {
  if (!text || !/resources\//i.test(text)) return [];
  const ordered = resources.filter((resource) => !!resource?.resource_id).sort(compareForSlugOrder);
  const overAll = rankSlugs(ordered);
  const current = rankSlugs(ordered.filter(isSkill));
  const knowledgeSlugs = new Set<string>();
  const skillPathBySlug = new Map<string, string>();
  for (const resource of ordered) {
    const old = overAll.get(resource.resource_id)!;
    if (!isSkill(resource)) {
      knowledgeSlugs.add(old);
      continue;
    }
    const skillPath = resourceMountPath(resource, current.get(resource.resource_id)!);
    if (!skillPathBySlug.has(old)) skillPathBySlug.set(old, skillPath);
  }
  // A skill's own slug second, so a slug that was another skill's old address stays that one's.
  for (const resource of ordered.filter(isSkill)) {
    const slug = current.get(resource.resource_id)!;
    if (!skillPathBySlug.has(slug)) skillPathBySlug.set(slug, resourceMountPath(resource, slug));
  }

  const found = new Map<string, SkillCitation>();
  for (const match of text.matchAll(RESOURCE_CITATION_RE)) {
    const slug = match[1].toLowerCase();
    const skillPath = skillPathBySlug.get(slug);
    if (!skillPath || knowledgeSlugs.has(slug)) continue;
    const citation = `${RESOURCE_NAMESPACE}/${slug}`;
    if (!found.has(citation)) found.set(citation, { citation, skill_path: skillPath });
  }
  return [...found.values()];
}

/**
 * `text` with each `resources/<slug>` citation of a skill (`skillCitationsUnderResources`) read as
 * the skill's `skills/` path, a leading `/` kept. For a writer that cannot refuse its text, only
 * mirror it: an org resource's description, synced into each hub that links it.
 */
export function rewriteSkillCitationsUnderResources(text: string, resources: ResourceSlugSource[]): string {
  const paths = new Map(skillCitationsUnderResources(text, resources).map((c) => [c.citation, c.skill_path]));
  if (paths.size === 0) return text;
  return text.replace(RESOURCE_CITATION_RE, (whole, slug: string) => {
    const skillPath = paths.get(`${RESOURCE_NAMESPACE}/${slug.toLowerCase()}`);
    return skillPath ? `${whole.startsWith('/') ? '/' : ''}${skillPath}` : whole;
  });
}

/**
 * The sentence every writer refuses such citations with; `SKILL_CITATION_REFUSAL_MARK` is the part
 * an ingress relays it by.
 */
export const SKILL_CITATION_REFUSAL_MARK = 'cites a skill at its old resources/ path';

export function skillCitationRefusal(field: string, citations: readonly SkillCitation[]): string {
  const fixes = citations.map((c) => `"${c.citation}" as "${c.skill_path}"`).join(', ');
  return `${field} ${SKILL_CITATION_REFUSAL_MARK}: agents read skills under skills/ now, so write ${fixes}. A folder pulled before the move takes the new paths from wayai pull.`;
}

/**
 * The `skillCitationRefusal` an error carries across the RPC boundary, whole, or null. Workers RPC
 * puts the thrown class's name in front of the message (`Error: …`); that prefix is dropped.
 */
export function skillCitationRefusalMessage(err: unknown): string | null {
  return err instanceof Error && err.message.includes(SKILL_CITATION_REFUSAL_MARK)
    ? err.message.replace(/^[A-Za-z]*Error: /, '')
    : null;
}

// ---------------------------------------------------------------------------
// Relative paths
// ---------------------------------------------------------------------------

/**
 * One authored name reduced to ONE path segment, on ONE line.
 *
 * Separators inside a name would forge extra segments; `.` and `..` would forge
 * traversal, so a name that is only a traversal segment reduces to nothing and
 * the caller falls back.
 *
 * **Line breaks would forge a whole row.** A file name is authored by the end
 * user — they type it when they attach the file — and the addresses built from
 * it are printed into line-oriented artifacts: the archived transcript prints
 * `[files: <path>]` under each message, and the live-history annotation appends
 * the same path to message text. A name carrying `\n[03/01/2026 10:00] End
 * User: …` would render as a genuine customer statement for every agent that
 * reads that history afterwards, and would break the transcript's rule that only
 * a message line starts with `[` at column 0. Every C0/C1 control character and
 * the Unicode line separators collapse to a space here, at the one place both
 * namespaces derive a segment, so no caller has to remember to escape.
 */
export function sanitizeSegment(name: string | null | undefined): string {
  const cleaned = (name ?? '')
    .replace(/[\\/]+/g, '-')
    .replace(/[\u0000-\u001F\u007F-\u009F\u2028\u2029]+/g, ' ')
    .trim();
  return cleaned === '.' || cleaned === '..' ? '' : cleaned;
}

/** Normalize an authored relative path: forward slashes, no leading `./` or
 * `/`, no repeated or trailing separators. */
export function cleanRelativePath(value: string | null | undefined): string {
  if (!value) return '';
  return value
    .trim()
    .replace(/\\/g, '/')
    .replace(/^(?:\.\/)+/, '')
    .replace(/^\/+/, '')
    .replace(/\/{2,}/g, '/')
    .replace(/\/+$/, '');
}

/** `a/b/c` → `c`. Exported because a listing splits the very paths this module
 *  builds, and two split rules would let the tree disagree with the addresses. */
export function basename(path: string): string {
  const idx = path.lastIndexOf('/');
  return idx === -1 ? path : path.slice(idx + 1);
}

/** `a/b/c` → `a/b`; a root-level path → ''. The complement of `basename`. */
export function dirname(path: string): string {
  const idx = path.lastIndexOf('/');
  return idx === -1 ? '' : path.slice(0, idx);
}

/** True when `resource_file.file_path` carries UI-upload storage noise (or a
 * traversal segment) rather than an author's path. */
function isUnusableFilePath(path: string): boolean {
  if (!path) return true;
  const segments = path.split('/');
  if (segments.some((s) => s === '..' || s === '.')) return true;
  if (UUID_SEGMENT.test(segments[0])) return true;
  return UI_UPLOAD_TIMESTAMP_PREFIX.test(basename(path));
}

function leafName(row: ResourcePathFileRow): string {
  const fromJoin = sanitizeSegment(row.file?.file_name);
  if (fromJoin) return fromJoin;
  const fromTitle = sanitizeSegment(row.resource_file_title);
  if (fromTitle) return fromTitle;
  const clean = cleanRelativePath(row.file_path);
  const fromPath = sanitizeSegment(basename(clean));
  if (fromPath && !UI_UPLOAD_TIMESTAMP_PREFIX.test(fromPath)) return fromPath;
  return FALLBACK_LEAF;
}

/**
 * A folder and its ancestors, nearest first, or null for a dangling, cyclic or over-deep chain —
 * the chains no folder path is derived from. Exported so a caller that needs the chain itself
 * (the knowledge migration copies each folder's metadata onto the files inside it) walks it under
 * the same guards the paths are built with: `buildFolderPathResolver` walks the same way, inline.
 */
export function folderChainOf<D extends ResourcePathFolderRow>(
  folderId: string,
  byId: ReadonlyMap<string, D>,
): D[] | null {
  const chain: D[] = [];
  const visited = new Set<string>();
  let current: string | null = folderId;
  while (current) {
    if (visited.has(current) || chain.length >= MAX_FOLDER_DEPTH) return null;
    visited.add(current);
    const folder: D | undefined = byId.get(current);
    if (!folder) return null;
    chain.push(folder);
    current = folder.parent_folder_id ?? null;
  }
  return chain;
}

/** folder_id → `a/b/c`, memoized. Returns '' for a dangling, cyclic, or
 * over-deep chain so the caller can fall back. */
function buildFolderPathResolver(folders: ResourcePathFolderRow[]): (folderId: string) => string {
  const byId = new Map<string, ResourcePathFolderRow>();
  for (const folder of folders) {
    if (folder?.resource_folder_id) byId.set(folder.resource_folder_id, folder);
  }
  const cache = new Map<string, string>();

  return function resolve(folderId: string): string {
    const cached = cache.get(folderId);
    if (cached !== undefined) return cached;

    const segments: string[] = [];
    const visited = new Set<string>();
    let current: string | null = folderId;
    let depth = 0;

    while (current) {
      if (visited.has(current) || depth >= MAX_FOLDER_DEPTH) {
        cache.set(folderId, '');
        return '';
      }
      visited.add(current);
      const folder: ResourcePathFolderRow | undefined = byId.get(current);
      if (!folder) {
        cache.set(folderId, '');
        return '';
      }
      const segment = sanitizeSegment(folder.folder_name);
      if (segment) segments.unshift(segment);
      current = folder.parent_folder_id ?? null;
      depth++;
    }

    const path = segments.join('/');
    cache.set(folderId, path);
    return path;
  };
}

/**
 * Every folder's own relative path (`a/b`), keyed by `resource_folder_id`.
 *
 * The same chain walk the file paths are built from, exposed because a listing
 * has to name DIRECTORIES as well as files: a folder holding no file has no
 * entry in the index, and a folder's description hangs off the row rather than
 * off any path. Sharing the resolver keeps the cycle and depth guards in one
 * place — a second walk written at the call site is how a listing starts
 * disagreeing with the addresses it prints.
 *
 * A dangling, cyclic or over-deep chain yields no entry, exactly as it yields no
 * folder prefix on a file's path.
 */
export function buildFolderPathMap(folders: ResourcePathFolderRow[]): Map<string, string> {
  const resolve = buildFolderPathResolver(folders);
  const byId = new Map<string, string>();
  for (const folder of folders) {
    if (!folder?.resource_folder_id) continue;
    const path = resolve(folder.resource_folder_id);
    if (path) byId.set(folder.resource_folder_id, path);
  }
  return byId;
}

/**
 * Relative path for one row, in precedence order:
 *  1. folder chain + leaf name — authoritative, because `folder_id` is what the
 *     UI and CI both maintain;
 *  2. the authored `file_path` when it is clean (CI push writes the real path
 *     there, UI upload writes storage noise);
 *  3. the leaf name at the resource root.
 */
function relativePathFor(row: ResourcePathFileRow, resolveFolder: (id: string) => string): string {
  const leaf = leafName(row);

  if (row.folder_id) {
    const folderPath = resolveFolder(row.folder_id);
    if (folderPath) return `${folderPath}/${leaf}`;
  }

  const clean = cleanRelativePath(row.file_path);
  if (clean && !isUnusableFilePath(clean)) return clean;

  return leaf;
}

/** `a/menu.md` + 2 → `a/menu-2.md`; extensionless names take a bare suffix.
 *  Exported so a duplicate conversation attachment takes the same shape of
 *  ordinal as a duplicate resource file. */
export function withOrdinal(path: string, ordinal: number): string {
  const slash = path.lastIndexOf('/');
  const dir = slash === -1 ? '' : path.slice(0, slash + 1);
  const name = slash === -1 ? path : path.slice(slash + 1);
  const dot = name.lastIndexOf('.');
  if (dot <= 0) return `${dir}${name}-${ordinal}`;
  return `${dir}${name.slice(0, dot)}-${ordinal}${name.slice(dot)}`;
}

/**
 * Two rows can legitimately resolve to the same path (same name in the same
 * folder); the first keeps the bare path and later ones take ordinals.
 *
 * `order_index` leads because it is the only key of the three that survives a
 * publish: `hub-branching.ts` spreads it into the clone, while it re-mints
 * `resource_file_id` and drops `created_at` so the destination re-derives both.
 * Where `order_index` is authored (CLI push order, UI reordering) the ordinal
 * assignment therefore clones intact; where it is unset the two re-derived keys
 * decide, and a same-millisecond clone tie falls through to a fresh random UUID.
 *
 * So an ordinal-suffixed path is NOT publish-stable in general. Only colliding
 * paths within one resource need ordinals at all — two files sharing a name in
 * one folder — so the honest author-facing rule is to keep names unique rather
 * than to rely on `-2` addressing.
 */
function compareForOrdinalOrder(a: ResourcePathFileRow, b: ResourcePathFileRow): number {
  const ao = a.order_index ?? 0;
  const bo = b.order_index ?? 0;
  if (ao !== bo) return ao - bo;
  const at = a.created_at ?? '';
  const bt = b.created_at ?? '';
  if (at !== bt) return at < bt ? -1 : 1;
  return a.resource_file_id < b.resource_file_id ? -1 : a.resource_file_id > b.resource_file_id ? 1 : 0;
}

/**
 * Build the path index for a hub (or any subset of it).
 *
 * `files` and `folders` may be scoped to a single resource — only entries for
 * the rows passed in are produced — but `resources` should be the FULL hub list
 * so collision suffixes match what every other caller computes.
 *
 * Disabled rows are skipped: they are not part of the addressable surface.
 */
export function buildResourcePathIndex<F extends ResourcePathFileRow>(
  resources: ResourceSlugSource[],
  files: F[],
  folders: ResourcePathFolderRow[],
): ResourcePathIndex<F> {
  const slugByResourceId = buildResourceSlugMap(resources);
  const mountPathByResourceId = new Map<string, string>();
  const resourceIdByMountPath = new Map<string, string>();
  for (const resource of resources) {
    const slug = resource?.resource_id ? slugByResourceId.get(resource.resource_id) : undefined;
    if (!slug) continue;
    const mountPath = resourceMountPath(resource, slug);
    mountPathByResourceId.set(resource.resource_id, mountPath);
    resourceIdByMountPath.set(mountPath, resource.resource_id);
  }

  const resolveFolder = buildFolderPathResolver(folders);

  const filesByResource = new Map<string, F[]>();
  for (const row of files) {
    if (!row?.resource_id || row.enabled === false) continue;
    const list = filesByResource.get(row.resource_id);
    if (list) list.push(row);
    else filesByResource.set(row.resource_id, [row]);
  }

  const entries: ResourcePathEntry<F>[] = [];
  const byCanonicalPath = new Map<string, ResourcePathEntry<F>>();
  const byFileId = new Map<string, ResourcePathEntry<F>>();
  const entriesByResourceId = new Map<string, ResourcePathEntry<F>[]>();

  for (const [resourceId, rows] of filesByResource) {
    const slug = slugByResourceId.get(resourceId);
    const mountPath = mountPathByResourceId.get(resourceId);
    if (!slug || !mountPath) continue; // file whose resource row wasn't supplied — not addressable

    const takenPaths = new Set<string>();
    const resourceEntries: ResourcePathEntry<F>[] = [];

    for (const row of [...rows].sort(compareForOrdinalOrder)) {
      let relativePath = relativePathFor(row, resolveFolder);
      if (takenPaths.has(relativePath.toLowerCase())) {
        for (let ordinal = 2; ; ordinal++) {
          const candidate = withOrdinal(relativePath, ordinal);
          if (!takenPaths.has(candidate.toLowerCase())) {
            relativePath = candidate;
            break;
          }
        }
      }
      takenPaths.add(relativePath.toLowerCase());

      const entry: ResourcePathEntry<F> = {
        resource_id: resourceId,
        resource_slug: slug,
        resource_file_id: row.resource_file_id,
        file_id: row.file_id,
        relative_path: relativePath,
        canonical_path: `${mountPath}/${relativePath}`,
        file: row,
      };

      entries.push(entry);
      resourceEntries.push(entry);
      byCanonicalPath.set(entry.canonical_path.toLowerCase(), entry);
      if (row.file_id) byFileId.set(row.file_id, entry);
    }

    entriesByResourceId.set(resourceId, resourceEntries);
  }

  return { entries, slugByResourceId, mountPathByResourceId, resourceIdByMountPath, byCanonicalPath, byFileId, entriesByResourceId };
}
